import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronRight as ChevronRightIcon, Home, MapPin, Bath, BedDouble, Trees, X, ChevronLeft, Star, ZoomIn, Users2, Wrench } from 'lucide-react';

const useScrollAnimation = (threshold = 0.08) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

// ==================== DATA ====================

const featuredProjects = [
  {
    id: 'casadonaji',
    name: 'Casa Donají',
    tagline: 'Iluminación natural optimizada · Bajo mantenimiento',
    recommended: true,
    clientType: 'Cliente particular',
    workType: 'Vivienda residencial · 2 niveles',
    location: 'Oaxaca · 2023',
    benefits: ['Bajo mantenimiento garantizado', 'Iluminación natural en todos los espacios', 'Diseño atemporal que no pasa de moda'],
    description:
      'Casa de dos niveles con diseño minimalista en tonos blancos, escaleras de concreto pulido, puertas de madera oscura con acabado contemporáneo, amplias habitaciones iluminadas y terraza con vista panorámica.',
    style: 'Arquitectura Minimalista',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/1hcmfx0i_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-12.jpg', label: 'Escaleras y Acceso' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/3it97mhg_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-13.jpg', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/s1l9c1d9_4.jpg', label: 'Habitación con Balcón' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/07ihrnvt_10.jpg', label: 'Área de Lavado' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/rnq9jx13_7_2.jpg', label: 'Pasillos y Puertas' },
    ],
    specs: [
      { icon: BedDouble, label: '4 Habitaciones' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Bath, label: '3 Baños' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Terraza' },
    ],
  },
  {
    id: 'casatutla',
    name: 'Casa Tutla',
    tagline: 'Cocina equipada · Espacios bien distribuidos',
    clientType: 'Cliente particular',
    workType: 'Vivienda residencial · 2 niveles',
    location: 'Oaxaca · 2023',
    benefits: ['Cocina integral lista para usar', 'Balcón con barandal de acero inoxidable', 'Distribución funcional para familias'],
    description:
      'Casa de dos niveles con fachada elegante en tonos blanco y rojo, puerta principal con diseño contemporáneo, balcón con barandal de acero inoxidable, cocina equipada con gabinetes rojos y barra desayunadora.',
    style: 'Arquitectura Residencial Moderna',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/bgkti7iy_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-14.jpg', label: 'Fachada Principal' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/hitfrgef_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-15.jpg', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/5qwsogwp_14.jpg', label: 'Vista Exterior' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/7udcsik3_1_2.jpg', label: 'Cocina Equipada' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/px4p6h5r_2_1.jpg', label: 'Sala y Escaleras' },
    ],
    specs: [
      { icon: BedDouble, label: '3 Habitaciones' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Bath, label: '2 Baños' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Terraza' },
    ],
  },
  {
    id: 'xoxocotlan',
    name: 'Xoxocotlán',
    tagline: 'Fachada imponente · Terraza y acabados premium',
    clientType: 'Cliente particular',
    workType: 'Vivienda residencial · 2 niveles',
    location: 'Xoxocotlán, Oaxaca · 2023',
    benefits: ['Terraza con vista panorámica', 'Acabados de calidad premium', 'Fachada de alto impacto visual'],
    description:
      'Vivienda de dos niveles con impresionante fachada en color rojo intenso, diseño moderno con balcón y terraza, amplios espacios interiores con acabados de calidad, puertas de madera y ventanería de aluminio.',
    style: 'Arquitectura Contemporánea',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/ohpo17ox_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-16.jpg', label: 'Fachada Principal' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/gm9mpw2f_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-17.jpg', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/8h2zuto7_m5.jpg', label: 'Cocina con Barra' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/i8ot2sba_m9.jpg', label: 'Habitación y Terraza' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/sgx1wl8u_m4.jpg', label: 'Vista Exterior' },
    ],
    specs: [
      { icon: BedDouble, label: '4 Habitaciones' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Bath, label: '3 Baños' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Terraza' },
    ],
  },
  {
    id: 'santarosa',
    name: 'Santa Rosa',
    tagline: 'Herrería artesanal · Pisos de madera y luz natural',
    clientType: 'Cliente particular',
    workType: 'Vivienda residencial · 2 niveles',
    location: 'Santa Rosa, Oaxaca · 2022',
    benefits: ['Herrería artesanal exclusiva', 'Pisos de madera laminada de alta durabilidad', 'Óptima iluminación natural en cada espacio'],
    description:
      'Amplia residencia de dos niveles con acabados modernos, herrería artesanal decorativa, pisos de madera laminada, baños con azulejo estilo madera y excelente iluminación natural en todos los espacios.',
    style: 'Arquitectura Residencial Moderna',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/m5z8nkma_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-18.jpg', label: 'Escaleras y Acceso' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/zo7srksz_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-19.jpg', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/norsc0v0_IMG-20220810-WA0068.jpg', label: 'Habitación Principal' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/dlqac5qs_IMG-20220926-WA0032.jpg', label: 'Sala y Escaleras' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/2xhwfvtp_IMG-20220926-WA0045.jpg', label: 'Baño con Acabados' },
    ],
    specs: [
      { icon: BedDouble, label: '4 Habitaciones' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Bath, label: '3 Baños' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Terraza' },
    ],
  },
  {
    id: 'tule4',
    name: 'Santa María del Tule IV',
    tagline: 'Personalidad única · Acabados artesanales mexicanos',
    clientType: 'Cliente particular',
    workType: 'Vivienda residencial · 2 niveles',
    location: 'Santa María del Tule, Oaxaca · 2022',
    benefits: ['Identidad visual única y memorable', 'Acabados artesanales de la región', 'Excelente valor de inversión a largo plazo'],
    description:
      'Vivienda de dos niveles con fachada distintiva en color magenta, diseño moderno con herrería artesanal decorativa, amplios espacios interiores con acabados de calidad y excelente iluminación natural.',
    style: 'Arquitectura Contemporánea Mexicana',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/d1iq31xh_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-20.jpg', label: 'Recámara con Balcón' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/pjlw8ncq_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-21.jpg', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/w3xgt0uf_6.png', label: 'Fachada Principal' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/x1on7v2f_IMG-20221205-WA0027.jpg', label: 'Escaleras con Herrería' },
      { url: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/94yufopu_IMG-20221205-WA0029.jpg', label: 'Cocina Integral' },
    ],
    specs: [
      { icon: BedDouble, label: '3 Habitaciones' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Bath, label: '3 Baños ½' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Terraza' },
    ],
  },
  {
    id: 'tule2',
    name: 'Santa María del Tule II',
    tagline: 'Alberca privada · Doble altura y áreas verdes',
    clientType: 'Cliente particular',
    workType: 'Residencia de lujo · Alberca privada',
    location: 'Santa María del Tule, Oaxaca · 2023',
    benefits: ['Alberca privada incluida', 'Doble altura con gran luminosidad', 'Áreas verdes y jardín privado'],
    description:
      'Residencia de lujo con diseño minimalista moderno, grandes ventanales, doble altura, cocina equipada de primer nivel, alberca privada y amplias áreas verdes.',
    style: 'Arquitectura Contemporánea',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/ccmk3xor_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-05.jpg', label: 'Fachada Principal' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/q7hhk3dz_6_1.png', label: 'Área Exterior y Alberca' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/wjvwoa9u_3.png', label: 'Cocina Equipada' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/lhnnfgql_2_1.png', label: 'Doble Altura / Sala' },
    ],
    specs: [
      { icon: BedDouble, label: '3 Recámaras' },
      { icon: Home, label: 'Sala y Comedor' },
      { icon: Home, label: 'Cocina Equipada' },
      { icon: Bath, label: '3 Baños ½' },
      { icon: Trees, label: 'Áreas Verdes' },
      { icon: Home, label: 'Terraza' },
      { icon: Home, label: 'Estudio' },
      { icon: Home, label: 'Área de Lavado' },
    ],
  },
  {
    id: 'tule1',
    name: 'Santa María del Tule',
    tagline: 'Estilo oaxaqueño · Jardín nativo y puertas panorámicas',
    clientType: 'Cliente particular',
    workType: 'Vivienda estilo regional · Arquitectura oaxaqueña',
    location: 'Santa María del Tule, Oaxaca · 2023',
    benefits: ['Jardín con vegetación nativa (bajo riego)', 'Acabados artesanales oaxaqueños auténticos', 'Puertas panorámicas de cristal para máxima luz'],
    description:
      'Residencia estilo hacienda oaxaqueña con jardín de vegetación nativa, amplios espacios interiores con puertas de cristal panorámicas y acabados artesanales de la región.',
    style: 'Arquitectura Regional Oaxaqueña',
    gallery: [
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/lglahx5u_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-03.jpg', label: 'Fachada y Jardín' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/5rulydg5_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-04.jpg', label: 'Vista Exterior' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/y8199o6y_16.png', label: 'Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/awhdd3f2_19.png', label: 'Espacios Interiores' },
      { url: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/lbguxuqp_22.png', label: 'Detalles y Acabados' },
    ],
    specs: [
      { icon: BedDouble, label: '2 Recámaras' },
      { icon: Home, label: 'Sala' },
      { icon: Home, label: 'Comedor' },
      { icon: Home, label: 'Cocina' },
      { icon: Home, label: 'Estudio' },
      { icon: Bath, label: '2 Baños ½' },
      { icon: Home, label: 'Área de Lavado' },
      { icon: Trees, label: 'Áreas Verdes' },
    ],
  },
];

// ==================== FIXED GALLERY CARD ====================

const FeaturedCard = ({ project }) => {
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => { setActiveImg(0); }, [project.id]);

  const prevImg = () => setActiveImg((p) => (p - 1 + project.gallery.length) % project.gallery.length);
  const nextImg = () => setActiveImg((p) => (p + 1) % project.gallery.length);

  return (
    <>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: '1.15fr 1fr',
        border: '1px solid var(--border-color)',
        boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
      }} className="featured-project-grid">

        {/* ===== LEFT: GALLERY ===== */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 500, background: 'var(--gray-light)' }}>

          {/* Main image area */}
          <div
            style={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'zoom-in',
              minHeight: 380,
            }}
            onClick={() => setLightbox(activeImg)}
          >
            <img
              src={project.gallery[activeImg].url}
              alt={project.gallery[activeImg].label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                transition: 'transform 0.6s ease',
              }}
              onMouseEnter={(e) => { e.target.style.transform = 'scale(1.04)'; }}
              onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}
            />

            {/* Gradient overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, transparent 40%, rgba(0,0,0,0.4) 100%)',
              pointerEvents: 'none',
            }} />

            {/* Zoom hint */}
            <div style={{
              position: 'absolute', top: '1rem', right: '1rem',
              background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
              color: 'var(--text-primary)',
              display: 'flex', alignItems: 'center', gap: '0.3rem',
              fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.65rem',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '0.35rem 0.65rem', borderRadius: 20,
              border: '1px solid var(--border-color)',
              pointerEvents: 'none',
            }}>
              <ZoomIn size={11} /> {activeImg + 1}/{project.gallery.length}
            </div>

            {/* Image label */}
            <div style={{
              position: 'absolute', bottom: '1rem', left: '1rem',
              background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)',
              color: 'var(--text-primary)', fontFamily: 'var(--font-heading)', fontWeight: 600,
              fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '0.3rem 0.7rem', borderRadius: 4,
              border: '1px solid var(--border-color)',
              pointerEvents: 'none',
            }}>
              {project.gallery[activeImg].label}
            </div>

            {/* Prev arrow */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImg(); }}
              style={{
                position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.9)', border: '1px solid var(--border-color)',
                borderRadius: '50%', width: 38, height: 38,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--text-primary)', transition: 'background 0.25s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--red)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-card)'}
            ><ChevronLeft size={17} /></button>

            {/* Next arrow */}
            <button
              onClick={(e) => { e.stopPropagation(); nextImg(); }}
              style={{
                position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.9)', border: '1px solid var(--border-color)',
                borderRadius: '50%', width: 38, height: 38,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--text-primary)', transition: 'background 0.25s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--red)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-card)'}
            ><ChevronRightIcon size={17} /></button>
          </div>

          {/* Thumbnail strip */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            background: 'var(--bg-secondary)',
            overflowX: 'auto',
            alignItems: 'center',
            flexShrink: 0,
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--red) transparent',
          }}>
            {project.gallery.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                style={{
                  flexShrink: 0,
                  width: 68,
                  height: 48,
                  borderRadius: 7,
                  overflow: 'hidden',
                  border: i === activeImg
                    ? '2px solid var(--red)'
                    : '2px solid var(--border-color)',
                  cursor: 'pointer',
                  padding: 0,
                  background: 'none',
                  transition: 'border-color 0.25s ease, transform 0.2s ease',
                  transform: i === activeImg ? 'scale(1.06)' : 'scale(1)',
                  boxShadow: i === activeImg ? '0 0 0 1px var(--red)' : 'none',
                }}
              >
                <img
                  src={img.url}
                  alt={img.label}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* ===== RIGHT: INFO ===== */}
        <div style={{ padding: '2.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            {/* Badges */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                background: 'var(--red)', color: 'white',
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                padding: '0.3rem 0.75rem', borderRadius: 4,
              }}>
                <Star size={10} fill="white" strokeWidth={0} /> Proyecto Destacado
              </span>
              {project.recommended && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: '#f59e0b', color: 'white',
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                  padding: '0.3rem 0.75rem', borderRadius: 4,
                }}>
                  ⭐ Recomendado
                </span>
              )}
            </div>

            <h3 style={{
              fontFamily: 'var(--font-heading)', fontWeight: 900,
              fontSize: 'clamp(1.4rem, 2.3vw, 2rem)',
              color: 'var(--text-primary)', lineHeight: 1.1, marginBottom: '0.35rem',
            }}>
              {project.name}
            </h3>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: '0.78rem',
              color: 'var(--red)', fontWeight: 600, marginBottom: '0.5rem', lineHeight: 1.4,
            }}>
              {project.tagline}
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem' }}>
              <MapPin size={13} color="var(--red)" />
              <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--gray)' }}>
                {project.location.split(' · ')[0]}
              </span>
            </div>

            {(project.clientType || project.workType) && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                {project.clientType && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                    borderRadius: 20, padding: '0.25rem 0.7rem',
                    fontFamily: 'var(--font-heading)', fontWeight: 600,
                    fontSize: '0.65rem', letterSpacing: '0.06em', color: 'var(--gray-dark)',
                  }}>
                    <Users2 size={11} color="var(--red)" strokeWidth={2} />
                    {project.clientType}
                  </span>
                )}
                {project.workType && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    background: 'rgba(196,30,58,0.06)', border: '1px solid rgba(196,30,58,0.18)',
                    borderRadius: 20, padding: '0.25rem 0.7rem',
                    fontFamily: 'var(--font-heading)', fontWeight: 600,
                    fontSize: '0.65rem', letterSpacing: '0.06em', color: 'var(--red)',
                  }}>
                    <Wrench size={11} color="var(--red)" strokeWidth={2} />
                    {project.workType}
                  </span>
                )}
              </div>
            )}

            <p style={{
              fontFamily: 'var(--font-body)', fontSize: '0.875rem',
              color: 'var(--gray-dark)', lineHeight: 1.8, marginBottom: '1rem',
              textAlign: 'justify',
            }}>
              {project.description}
            </p>

            {/* Benefits list */}
            {project.benefits && (
              <div style={{ marginBottom: '1.5rem' }}>
                {project.benefits.map((benefit, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
                    <span style={{ color: 'var(--red)', fontWeight: 800, fontSize: '0.9rem', flexShrink: 0 }}>✔</span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--gray-dark)' }}>{benefit}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Specs grid */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '0.55rem', marginBottom: '2rem',
            }} className="specs-grid">
              {project.specs.map((spec, i) => (
                <div key={i} style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 8, padding: '0.6rem 0.4rem', textAlign: 'center',
                  transition: 'background 0.25s ease, border-color 0.25s ease', cursor: 'default',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(196,30,58,0.06)'; e.currentTarget.style.borderColor = 'rgba(196,30,58,0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-secondary)'; e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                    <spec.icon size={14} color="var(--red)" strokeWidth={1.5} />
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.6rem', color: 'var(--gray)', lineHeight: 1.3 }}>
                    {spec.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-red"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}
          >
            Quiero un proyecto como este
          </button>
        </div>
      </div>

      {/* ===== LIGHTBOX ===== */}
      {lightbox !== null && ReactDOM.createPortal(
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: '#000',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Close button */}
          <button 
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }} 
            style={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--red)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <X size={24} />
          </button>

          {/* Image label */}
          <div style={{
            position: 'absolute',
            top: '1.5rem',
            left: '1.5rem',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            color: 'white',
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            padding: '0.6rem 1rem',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.2)',
          }}>
            {project.gallery[lightbox].label}
          </div>

          {/* Main content area */}
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2rem',
              padding: '0 6rem',
              width: '100%',
              maxHeight: '70vh',
            }}
          >
            {/* Prev arrow */}
            <button 
              onClick={() => setLightbox((p) => (p - 1 + project.gallery.length) % project.gallery.length)} 
              style={{
                flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--red)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <ChevronLeft size={26} />
            </button>

            {/* Image */}
            <img
              src={project.gallery[lightbox].url}
              alt={project.gallery[lightbox].label}
              style={{
                maxWidth: 'calc(100vw - 300px)',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 8,
                boxShadow: '0 20px 80px rgba(0,0,0,0.8)',
              }}
            />

            {/* Next arrow */}
            <button 
              onClick={() => setLightbox((p) => (p + 1) % project.gallery.length)} 
              style={{
                flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '50%',
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--red)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
            >
              <ChevronRightIcon size={26} />
            </button>
          </div>

          {/* Thumbnail strip */}
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'center',
              marginTop: '2rem',
              padding: '0.75rem 1rem',
              background: 'rgba(0,0,0,0.6)',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {project.gallery.map((img, i) => (
              <button 
                key={i} 
                onClick={() => setLightbox(i)} 
                style={{
                  width: 60,
                  height: 42,
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: i === lightbox ? '2px solid var(--red)' : '2px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  padding: 0,
                  background: 'none',
                  transition: 'border-color 0.2s ease, transform 0.2s ease',
                  transform: i === lightbox ? 'scale(1.1)' : 'scale(1)',
                  flexShrink: 0,
                }}
              >
                <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>

          {/* Counter */}
          <div style={{
            marginTop: '0.75rem',
            color: 'rgba(255,255,255,0.6)',
            fontFamily: 'var(--font-heading)',
            fontSize: '0.75rem',
            letterSpacing: '0.1em',
          }}>
            {lightbox + 1} / {project.gallery.length}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ==================== MAIN SECTION ====================

const Projects = () => {
  const ref = useScrollAnimation();
  const [activeProject, setActiveProject] = useState(0);

  return (
    <section
      id="proyectos"
      ref={ref}
      style={{
        padding: '8rem 2rem',
        background: 'var(--bg-secondary)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Header */}
        <div className="fade-up" style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: '3rem', flexWrap: 'wrap', gap: '1rem',
        }}>
          <div>
            <div className="section-label">Portafolio</div>
            <h2 style={{
              fontFamily: 'var(--font-heading)', fontWeight: 900,
              fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', marginTop: '0.5rem',
            }}>
              Proyectos <span style={{ color: 'var(--red)' }}>Destacados</span>
            </h2>
          </div>
          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: 'none', border: 'none',
              fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.85rem',
              color: 'var(--red)', cursor: 'pointer', letterSpacing: '0.05em',
              textTransform: 'uppercase', padding: '0.5rem 0',
              borderBottom: '2px solid var(--red)', transition: 'gap 0.3s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.gap = '0.7rem'}
            onMouseLeave={(e) => e.currentTarget.style.gap = '0.4rem'}
          >
            Cotiza tu casa en 2 minutos <ChevronRightIcon size={16} />
          </button>
        </div>

        {/* Project Selector Tabs */}
        <div className="fade-up" style={{ display: 'flex', gap: '0.875rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {featuredProjects.map((p, i) => (
            <button
              key={p.id}
              onClick={() => setActiveProject(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.875rem',
                padding: '0.875rem 1.5rem', borderRadius: 12,
                border: activeProject === i ? '2px solid var(--red)' : '2px solid var(--border-color)',
                background: 'var(--bg-card)',
                cursor: 'pointer', transition: 'all 0.35s ease',
                boxShadow: activeProject === i ? '0 8px 32px rgba(196,30,58,0.15)' : '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{
                width: 52, height: 38, borderRadius: 7, overflow: 'hidden', flexShrink: 0,
                border: activeProject === i ? '1px solid rgba(196,30,58,0.2)' : '1px solid var(--border-color)',
              }}>
                <img src={p.gallery[0].url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left center' }} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.875rem',
                    color: activeProject === i ? 'var(--red)' : 'var(--dark)', lineHeight: 1.2,
                  }}>{p.name}</span>
                  {p.recommended && <span style={{ fontSize: '0.7rem' }}>⭐</span>}
                </div>
              </div>
              {activeProject === i && (
                <div style={{ width: 8, height: 8, background: 'var(--red)', borderRadius: '50%', flexShrink: 0, marginLeft: '0.25rem' }} />
              )}
            </button>
          ))}
        </div>

        {/* Active Featured Project */}
        <div className="fade-up">
          <FeaturedCard project={featuredProjects[activeProject]} />
        </div>

      </div>

      <style>{`
        .featured-project-grid { grid-template-columns: 1.15fr 1fr !important; }
        @media (max-width: 950px) {
          .featured-project-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          #proyectos { padding-top: 5rem !important; padding-bottom: 5rem !important; }
          .project-tabs-btn img { display: none !important; }
          .specs-grid { grid-template-columns: repeat(4, 1fr) !important; gap: 0.4rem !important; }
        }
      `}</style>
    </section>
  );
};

export default Projects;
